import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetAvailabilityScheduleService } from 'app/_services/asset-availability-schedule.service';
import { NotificationService } from 'app/_services/notification.service';
import { ScheduleValidationService } from 'app/_services/schedule-validation.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'edit-availability-modal',
  templateUrl: './edit-availability-modal.component.html'
})
export class EditAvailabilityModalComponent implements OnInit {
  public readonly directReasonMaxLength = 300;
  public readonly combinedReasonMaxLength = 300;

  @Input() assetId: any;
  @Input() scheduleItem: any;
  @Input() needsApproval: boolean;
  @Input() typeScheduleItem: any;

  public form: FormGroup;
  public validationErrors: any[] = [];
  public minDate = new Date();
  public saveInProgress = false;
  public endTimeShowChangeOccurred = false;
  public ignoreOverlap: boolean = false;

  constructor(
    private activeModal: NgbActiveModal,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    private scheduleValidationService: ScheduleValidationService,
    private assetAvailabilityScheduleService: AssetAvailabilityScheduleService,
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit() {
    this.minDate.setUTCHours(0, 0, 0, 0);

    const reasonValidators = this.needsApproval ? [Validators.required] : [Validators.maxLength(this.directReasonMaxLength)];
    const workersValidators = this.needsApproval ? [Validators.required] : [];

    this.form = this.formBuilder.group({
      scheduledStartTime: new FormControl(new Date(this.scheduleItem.startTime)),
      scheduledEndTime: new FormControl(new Date(this.scheduleItem.endTime)),
      reason: new FormControl(this.scheduleItem.reason, reasonValidators),
      workers: new FormControl('^', workersValidators),
    }, { validator: this.combinedReasonLengthValidator() });
  }

  public get combinedReasonLength(): number {
    return this.buildSubmittedReason(this.form.value).length;
  }

  // Update the end time Date when the start time changes, for convenience
  public onStartTimeChange() {
    const start: Date = this.form.get('scheduledStartTime').value;
    const end: Date = this.form.get('scheduledEndTime').value;

    if (start && !end) {
      this.form.patchValue({ scheduledEndTime: start });
      this.animateEndTimeField();
    }

    if (start && end) {
      if (start.getDay() !== end.getDay()) {
        const newEnd = new Date(start.getTime());
        newEnd.setHours(end.getHours(), end.getMinutes());

        this.form.patchValue({ scheduledEndTime: newEnd });
        this.animateEndTimeField();
      }
    }
  }

  private animateEndTimeField() {
    this.endTimeShowChangeOccurred = true;
    setTimeout(() => {
      this.endTimeShowChangeOccurred = false;
    }, 250);
  }

  public closeModal() {
    this.activeModal.dismiss();
  }

  public deleteScheduleItem() {
    this.saveInProgress = true;
    if (this.needsApproval) {
      this.requestDelete();
    } else {

      Swal.fire({
        title: `Warning `,
        text: `Are you sure you want to delete?`,
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-danger',
        },
        confirmButtonText: 'Yes, delete',
      }).then((result) => {
        if (result.value) {
          this.deleteSchedule();
        } else {
          this.saveInProgress = false;
        }
      });
    }
  }

  private async requestDelete() {
    if (this.typeScheduleItem === 'EDIT_PENDING_SCHEDULE') {
      // permitted to delete the pending task
      this.workflowItemService.deleteWorkflowItem(this.scheduleItem.id).subscribe(
        (data) => {
        this.notificationService.notify('success', 'Success', 'Request for schedule has been removed.');
        this.activeModal.close('refresh');
      });
    } else {
      const { value: reason } = await Swal.fire({
        titleText: 'Reason for cancellation',
        input: 'textarea',
        inputValidator: (value) => {
          if (value.length === 0) {
            return 'You need to enter a reason for cancelling';
          } else if (value.length > 300) {
            return 'Comments cannot be longer than 300 characters';
          }
        },
        inputAttributes: {
          'aria-label': 'Type your reason for cancelling the schedule here',
        },
        showCancelButton: true,
      });

      if (reason) {
        let body = {
          workflowType: 'ASSET_BOOKING_CANCELLATION',
          assetAvailabilityId: this.scheduleItem.id,
          reason: reason,
        };

        this.workflowItemService.createWorkflowItem(body).subscribe(() => {
          this.notificationService.notify('success', 'Success', 'Sent for Appoval');
          this.activeModal.close('refresh');
        });
      }
    }
  }

  private deleteSchedule() {
    this.assetAvailabilityScheduleService.deleteAvailabilityItem(this.scheduleItem.id).subscribe(() => {
      this.notificationService.notify('success', 'Success', 'Availability window has been removed.');
      this.activeModal.close('refresh');
      this.saveInProgress = false;
    }, error => {
      this.saveInProgress = false;
      let message = 'Schedule could not be deleted';
      this.processErrors(error, message);
    });
  }

  public onSubmit() {
    this.saveInProgress = true;
    const values = this.form.value;

    this.validationErrors = [];
    const validation = this.scheduleValidationService.validateForm(values);

    if (!validation.isValid) {
      this.validationErrors = validation.validationErrors;
      this.saveInProgress = false;
      return
    }

    let body = {
      workflowType: 'ASSET_BOOKING_REQUEST',
      assetId: this.assetId,
      startTime: values.scheduledStartTime,
      endTime: values.scheduledEndTime,
      reason: this.buildSubmittedReason(values),
      ignoreOverlap: this.ignoreOverlap,
    };

    if (this.typeScheduleItem === 'EDIT_PENDING_SCHEDULE') {
      this.workflowItemService.updateAssetAvailaibityWorkflowItem(this.scheduleItem.id, body).subscribe(
        (data) => {
          this.notificationService.notify('success', 'Success', 'Availability window has been updated.');
          this.activeModal.close('refresh');
        },
        error => {
          let message = 'Schedule could not be saved';
          this.processErrors(error, message);
        });
    }

    if (this.typeScheduleItem === 'EDIT_APPROVED_SCHEDULE') {
      let cancelBody = {
        workflowType: 'ASSET_BOOKING_CANCELLATION',
        assetAvailabilityId: this.scheduleItem.id,
        reason: 'EDITED',
      };

      this.workflowItemService.createWorkflowItem(body).subscribe(
        (data) => {
          this.workflowItemService.createWorkflowItem(cancelBody).subscribe(
            (data) => {
              this.notificationService.notify('success', 'Success', 'Sent for Appoval');
              this.activeModal.close('refresh');
            },
            (error) => {
              let message = 'Schedule could not be saved';
              this.processErrors(error, message);
            });
        },
        (error) => {
          let message = 'Schedule could not be saved';
          this.processErrors(error, message);
        });
    }
  }

  public editScheduleItem() {
    this.saveInProgress = true;
    if (this.needsApproval) {
      this.onSubmit();
    } else {
      this.editSchedule();
    }
  }

  private editSchedule() {
    this.saveInProgress = true;
    const values = this.form.value;
    let body = {
      assetId: this.assetId,
      startTime: values.scheduledStartTime,
      endTime: values.scheduledEndTime,
      reason: values.reason,
      ignoreOverlap: this.ignoreOverlap,
    };

    this.assetAvailabilityScheduleService.editAvailabilityItem(this.scheduleItem.id, body).subscribe(() => {
      this.notificationService.notify('success', 'Success', 'Availability window has been updated.');
      this.activeModal.close('refresh');
    }, error => {
      let message = 'Schedule could not be saved';
      this.processErrors(error, message);
    });
  }


  private processErrors(error: any, message: any) {
    this.saveInProgress = false;

    if (error && error.error && error.error.title) {
        message = error.error.title;

        if (message === 'Availability window overlaps existing window') {
          Swal.fire({
            title: `Warning `,
            text: `${message}. Are you sure you want to submit?`,
            type: 'warning',
            showCancelButton: true,
            customClass: {
              confirmButton: 'confirm-primary',
            },
            confirmButtonText: 'Yes, submit!',
          }).then((result) => {
            if (result.value) {
              this.ignoreOverlap = true;
              this.onSubmit();
            }
          });
        } else {
          this.notificationService.notify('danger', 'Error', message);
        }

      if (error.error.fieldErrors && error.error.fieldErrors[0] && error.error.fieldErrors[0].message) {
        message = error.error.fieldErrors[0].message;
        this.notificationService.notify('danger', 'Error', message);
      }

    } else {
      this.notificationService.notify('danger', 'Error', message);
    }
  }

  private combinedReasonLengthValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      if (!this.needsApproval) {
        return null;
      }

      return this.buildSubmittedReason(formGroup.value).length > this.combinedReasonMaxLength ? { combinedReasonTooLong: true } : null;
    };
  }

  private buildSubmittedReason(values: { reason: string; workers: string; }): string {
    return values.reason + ' ' + values.workers;
  }

}
